import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  /**
   * Callback to load more items
   */
  onLoadMore: () => void | Promise<void>;
  
  /**
   * Whether there are more items to load
   */
  hasMore: boolean;
  
  /**
   * Whether currently loading
   */
  isLoading: boolean;
  
  /**
   * Root element for intersection observer (the scrollable container)
   * If not provided, uses viewport
   */
  root?: Element | null;
  
  /**
   * Root margin for intersection observer
   * Default: "0px 0px 25% 0px" (triggers at 75% scroll)
   */
  rootMargin?: string;
  
  /**
   * Threshold for intersection observer
   * Default: 0.1
   */
  threshold?: number;
  
  /**
   * Enable/disable the infinite scroll
   */
  enabled?: boolean;
}

interface UseInfiniteScrollReturn {
  /**
   * Ref to attach to the sentinel element at the bottom of the list
   */
  sentinelRef: React.RefObject<HTMLDivElement>;
}

/**
 * Custom hook for infinite scroll using Intersection Observer
 * 
 * @example
 * const { sentinelRef } = useInfiniteScroll({
 *   onLoadMore: loadMoreConversations,
 *   hasMore: hasMoreConversations,
 *   isLoading: isLoadingMore,
 * });
 * 
 * // In JSX:
 * <div ref={sentinelRef} />
 */
export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
  root = null,
  rootMargin = '0px 0px 25% 0px', // Triggers at 75% scroll
  threshold = 0.1,
  enabled = true,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef(false);

  const handleIntersection = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      
      // Only trigger if:
      // 1. Element is intersecting
      // 2. There are more items to load
      // 3. Not currently loading
      // 4. Infinite scroll is enabled
      if (
        entry.isIntersecting &&
        hasMore &&
        !isLoading &&
        !loadingRef.current &&
        enabled
      ) {
        loadingRef.current = true;
        
        try {
          await onLoadMore();
        } catch (error) {
          console.error('Error loading more items:', error);
        } finally {
          // Add a small delay to prevent rapid successive calls
          setTimeout(() => {
            loadingRef.current = false;
          }, 100);
        }
      }
    },
    [hasMore, isLoading, onLoadMore, enabled]
  );

  useEffect(() => {
    const currentSentinel = sentinelRef.current;
    
    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Don't create observer if not enabled or no sentinel
    if (!enabled || !currentSentinel) {
      return;
    }

    // Create new Intersection Observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root, // Use provided root (scroll container) or viewport
      rootMargin,
      threshold,
    });

    // Start observing the sentinel element
    observerRef.current.observe(currentSentinel);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      loadingRef.current = false;
    };
  }, [handleIntersection, root, rootMargin, threshold, enabled]);

  // Sync loading ref with prop (both true and false states)
  useEffect(() => {
    loadingRef.current = isLoading;
  }, [isLoading]);

  return {
    sentinelRef,
  };
}

